import { useEffect, useRef, useState } from 'react';

/**
 * Рамка — один из трёх элементов фирменного стиля (брендбук, стр. 29, 34).
 * «Рамка здесь представляет образ окна, как способ показать суть проектов,
 * оставаясь гибкими и открытыми.»
 *
 * Геометрия — ступенчатое «окно»: верхняя и нижняя грани идут на двух
 * уровнях, переходы скруглены. Пропорции сняты с эталона в брендбуке.
 *
 * Путь строится в пикселях по фактическому размеру контейнера, поэтому
 * скругления и ступени не деформируются при любом соотношении сторон —
 * искажение фирменных форм брендбуком не допускается.
 */

/** Доли от ширины/высоты, снятые с эталонной рамки. */
const TOP_LEFT_LEVEL = 0.21;
const TOP_STEP_X = 0.79;
const BOTTOM_LEFT_LEVEL = 0.91;
const BOTTOM_STEP_X = 0.37;

const JOINT_RADIUS = 20;

type Point = [number, number];

/** Строит замкнутый путь по вершинам, скругляя каждый стык. */
function roundedPath(points: Point[], radius: number): string {
  const n = points.length;
  const out: string[] = [];

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const [inX, inY] = unit(curr, prev);
    const [outX, outY] = unit(curr, next);

    // Радиус не должен съедать больше половины любого из смежных отрезков.
    const r = Math.min(radius, dist(curr, prev) / 2, dist(curr, next) / 2);

    const start: Point = [curr[0] + inX * r, curr[1] + inY * r];
    const end: Point = [curr[0] + outX * r, curr[1] + outY * r];

    out.push(i === 0 ? `M${fmt(start)}` : `L${fmt(start)}`);
    if (r > 0.5) out.push(`Q${fmt(curr)} ${fmt(end)}`);
  }

  out.push('Z');
  return out.join(' ');
}

const dist = (a: Point, b: Point) => Math.hypot(b[0] - a[0], b[1] - a[1]);

function unit(from: Point, to: Point): Point {
  const d = dist(from, to) || 1;
  return [(to[0] - from[0]) / d, (to[1] - from[1]) / d];
}

const fmt = (p: Point) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`;

export function BrandFrame({ inset = 0, className }: { inset?: number; className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Меряем сразу, а не ждём первого срабатывания ResizeObserver:
    // иначе при отложенном колбэке рамка не появится вовсе.
    const measure = () => {
      const { width, height } = host.getBoundingClientRect();
      setSize((current) =>
        current.w === width && current.h === height ? current : { w: width, h: height },
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const w = size.w - inset * 2;
  const h = size.h - inset * 2;
  // Слишком мелкая рамка выглядит как шум — не рисуем.
  const visible = w > 120 && h > 90;

  const points: Point[] = [
    [0, h * TOP_LEFT_LEVEL],
    [w * TOP_STEP_X, h * TOP_LEFT_LEVEL],
    [w * TOP_STEP_X, 0],
    [w, 0],
    [w, h],
    [w * BOTTOM_STEP_X, h],
    [w * BOTTOM_STEP_X, h * BOTTOM_LEFT_LEVEL],
    [0, h * BOTTOM_LEFT_LEVEL],
  ];

  return (
    <div
      ref={hostRef}
      className={className}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      aria-hidden
    >
      {visible && (
        <svg
          className="brand-frame"
          width={size.w}
          height={size.h}
          viewBox={`0 0 ${size.w} ${size.h}`}
          fill="none"
        >
          <g transform={`translate(${inset} ${inset})`}>
            <path
              d={roundedPath(points, JOINT_RADIUS)}
              stroke="currentColor"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        </svg>
      )}
    </div>
  );
}
